"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import CustomArrowIcon from "@/public/custom-arrow.svg";
import { Button } from "@/components/ui/Button";
import { LanguagePicker } from "@/components/ui/LanguagePicker";
import { ASSESSMENT_TOOL_URL } from "@/constants/links";
import { cn } from "@/lib/utils";
import type { MainLayoutFloatingCtaQueryResult } from "@/sanity/schemas/lib/queries";

interface ScrollActionDockProps {
    text: MainLayoutFloatingCtaQueryResult;
}

export function ScrollActionDock({ text }: ScrollActionDockProps) {
    const pathname = usePathname();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const trigger = document.querySelector<HTMLElement>("[data-scroll-dock-trigger]");

        if (!trigger) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(!entry.isIntersecting);
            },
            {
                threshold: 0,
                rootMargin: "-84px 0px 0px 0px",
            },
        );

        observer.observe(trigger);

        return () => {
            observer.disconnect();
        };
    }, [pathname]);

    return (
        <div
            inert={!isVisible}
            className={cn(
                "pointer-events-none fixed inset-0 z-60 hidden transition-opacity duration-300 md:block",
                isVisible ? "opacity-100" : "opacity-0",
            )}
        >
            <div className="relative mx-auto flex h-full max-w-7xl items-end justify-between gap-4 px-5 pb-6 md:px-10 sm:pb-8">
                <div
                    className={cn(
                        "pointer-events-auto mx-auto flex w-full max-w-3xl justify-center transition-transform duration-300",
                        isVisible ? "translate-y-0" : "translate-y-4",
                    )}
                >
                    <div className="flex w-full flex-col items-start gap-3 rounded-[22px] border border-white/10 bg-ink/90 px-4 py-3 text-white backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
                        <p className="max-w-60 text-sm leading-140 text-white/70 sm:max-w-none">
                            {text.label}
                        </p>
                        <Button
                            href={ASSESSMENT_TOOL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="cta"
                            className="h-10 shrink-0 px-5 text-sm"
                        >
                            {text.button}
                            <CustomArrowIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                    </div>
                </div>

                <div
                    className={cn(
                        "pointer-events-auto shrink-0 transition-transform duration-300",
                        isVisible ? "translate-y-0" : "translate-y-4",
                    )}
                >
                    <LanguagePicker placement="top" />
                </div>
            </div>
        </div>
    );
}
